import { FunctionComponent, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import {
    type CriterionValue,
    type OffsetDirection,
    type OffsetUnit,
    type ScheduleTriggerConfig
} from '@spike/automation-model';
import { useAutomationOptions } from '../../hooks/useAutomationOptions';
import { blockNonInteger, clampInt } from './numericField';
import { anchorIcon } from '../Trigger/eventDefinitions';
import styles from './TriggerSidebar.module.scss';

interface Props {
    config: ScheduleTriggerConfig;
    onChange: (config: ScheduleTriggerConfig) => void;
    onEditConditions: () => void;
    onEditSubjectConditions: () => void;
    conditionChips: string[];
    subjectConditionChips: string[];
}

const SEND_AT_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
    const value = `${String(hour).padStart(2, '0')}:00`;
    const suffix = hour < 12 ? 'am' : 'pm';
    const display = hour % 12 === 0 ? 12 : hour % 12;

    return { value, label: `${display}:00${suffix}` };
});

const SchedulePanel: FunctionComponent<Props> = ({
    config,
    onChange,
    onEditConditions,
    onEditSubjectConditions,
    conditionChips,
    subjectConditionChips
}) => {
    const { groupedAnchors, anchors, subjectFieldsFor, subjectForAnchor } = useAutomationOptions();

    const selectedAnchor = useMemo(() => anchors.find(a => a.key === config.anchor), [anchors, config.anchor]);

    const update = (patch: Partial<ScheduleTriggerConfig>) => onChange({ ...config, ...patch });

    const updateOffset = (patch: Partial<ScheduleTriggerConfig['offset']>) =>
        update({ offset: { ...config.offset, ...patch } });

    const isAnnual = selectedAnchor?.recurrence === 'annual';

    // Hour offsets mean a duration, not a time of day — pinning them to a
    // wall clock would contradict "24 hours before".
    const supportsSendAt = config.offset.unit !== 'hours';

    const subjectFields = subjectFieldsFor(subjectForAnchor(config.anchor));

    const selectAnchor = (key: string) => {
        const anchor = anchors.find(a => a.key === key);

        update({
            anchor: key,
            recurrence: anchor?.recurrence ?? 'once',
            // Subject conditions are scoped to the anchor's record, so they
            // can't survive switching to an anchor that carries a different one.
            subjectConditions: []
        });
    };

    return (
        <>
            <div className={styles.section}>
                <div className={styles.sectionLabel}>When should this run?</div>
                <div className={styles.eventList}>
                    {groupedAnchors.map(group => (
                        <div
                            key={group.category}
                            className={styles.eventGroup}
                        >
                            <div className={styles.eventGroupLabel}>{group.label}</div>
                            {group.anchors.map(anchor => (
                                <div
                                    key={anchor.key}
                                    className={clsx(styles.eventRow, {
                                        [styles.eventRowSelected]: config.anchor === anchor.key
                                    })}
                                    onClick={() => selectAnchor(anchor.key)}
                                >
                                    <div
                                        className={clsx(styles.eventIconCircle, {
                                            [styles.eventIconSelected]: config.anchor === anchor.key
                                        })}
                                    >
                                        <FontAwesomeIcon icon={anchorIcon(anchor.key)} />
                                    </div>
                                    <div className={styles.eventInfo}>
                                        <span className={styles.eventLabel}>{anchor.label}</span>
                                        <span className={styles.eventDesc}>{anchor.description}</span>
                                    </div>
                                    <div
                                        className={clsx(styles.eventRadioDot, {
                                            [styles.eventRadioDotActive]: config.anchor === anchor.key
                                        })}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {config.anchor && (
                <>
                    <hr className={styles.divider} />

                    <div className={styles.section}>
                        <div className={styles.sectionLabel}>Timing</div>
                        <div className={styles.offsetRow}>
                            <input
                                type="number"
                                min={0}
                                max={999}
                                step={1}
                                inputMode="numeric"
                                className={styles.offsetAmount}
                                onKeyDown={blockNonInteger}
                                value={config.offset.amount}
                                // Selecting on focus is what makes typing replace the
                                // current value instead of appending to it.
                                onFocus={e => e.target.select()}
                                onChange={e => updateOffset({ amount: clampInt(e.target.value, { min: 0, max: 999 }) })}
                            />
                            <select
                                className={styles.selectField}
                                value={config.offset.unit}
                                onChange={e => updateOffset({ unit: e.target.value as OffsetUnit })}
                            >
                                <option value="hours">hour(s)</option>
                                <option value="days">day(s)</option>
                                <option value="weeks">week(s)</option>
                                <option value="months">month(s)</option>
                            </select>
                            <select
                                className={styles.selectField}
                                value={config.offset.direction}
                                onChange={e => updateOffset({ direction: e.target.value as OffsetDirection })}
                            >
                                <option value="before">before</option>
                                <option value="after">after</option>
                            </select>
                        </div>
                        <div className={styles.sectionSubLabel}>
                            {selectedAnchor?.label ?? 'the selected date'}
                            {isAnnual && ', every year'}
                        </div>
                    </div>

                    {supportsSendAt && (
                        <div className={styles.section}>
                            <div className={styles.sectionLabel}>Send at</div>
                            <select
                                className={styles.selectField}
                                value={config.sendAt ?? (isAnnual ? '09:00' : '')}
                                onChange={e => update({ sendAt: e.target.value || null })}
                            >
                                {!isAnnual && <option value="">Same time as the event</option>}
                                {SEND_AT_OPTIONS.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className={styles.sectionSubLabel}>
                                {isAnnual
                                    ? 'This date has no time of day, so pick when the message should go out.'
                                    : 'Without a set time, the message goes out at the same time of day as the event itself.'}
                            </div>
                        </div>
                    )}

                    <hr className={styles.divider} />

                    <div className={styles.section}>
                        <div className={styles.sectionRow}>
                            <div>
                                <div className={styles.sectionLabel}>Client conditions</div>
                                <div className={styles.sectionSubLabel}>Only send to clients who match</div>
                            </div>
                            <button
                                type="button"
                                className={styles.editButton}
                                onClick={onEditConditions}
                            >
                                {conditionChips.length > 0 ? 'Edit' : 'Add'}
                            </button>
                        </div>
                        <div className={styles.criteriaChips}>
                            {conditionChips.map(chip => (
                                <span
                                    key={chip}
                                    className={styles.criteriaChip}
                                >
                                    {chip}
                                </span>
                            ))}
                        </div>
                    </div>

                    {subjectFields.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.sectionRow}>
                                <div>
                                    <div className={styles.sectionLabel}>
                                        Conditions on the {subjectLabel(selectedAnchor?.subject)}
                                    </div>
                                    {/* The distinction that makes boarding-only flows possible: this is
                                        about the record that triggered the run, not the client's history. */}
                                    <div className={styles.sectionSubLabel}>
                                        Only send when the triggering {subjectLabel(selectedAnchor?.subject)} matches
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={styles.editButton}
                                    onClick={onEditSubjectConditions}
                                >
                                    {subjectConditionChips.length > 0 ? 'Edit' : 'Add'}
                                </button>
                            </div>
                            <div className={styles.criteriaChips}>
                                {subjectConditionChips.map(chip => (
                                    <span
                                        key={chip}
                                        className={styles.criteriaChip}
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

const subjectLabel = (subject?: string | null): string => {
    if (!subject) return 'record';

    return subject.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
};

export const emptyScheduleConfig = (): ScheduleTriggerConfig => ({
    kind: 'schedule',
    anchor: '',
    offset: { direction: 'before', amount: 1, unit: 'days' },
    recurrence: 'once',
    sendAt: null,
    conditions: [] as CriterionValue[],
    subjectConditions: [] as CriterionValue[]
});

export default SchedulePanel;
